import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import AppBar from "@mui/material/AppBar"
import { Grid, Skeleton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot } from "@fortawesome/pro-light-svg-icons"
import { AvatarMenu } from "../../../../shared/layouts/components/AvatarMenu"
import { Link, useMatches } from "react-router-dom"
import { isObjectWithProp } from "../../../../lib/lang"
import { useEffect, useState } from "react"
import { ContextDisplay } from "../../../context/components/ContextDisplay"
import { NotificationTopBarButton } from "../../../../shared/layouts/components/NotificationTopBar/NotificationTopBarButton"
import { SERVICE_CENTER_REPORTS_ROUTE } from "../../../../constants/routes"
import FacilitynetLogo from "../../../../shared/layouts/assets/facilit-net-logo.svg"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { fetchServiceCenterDetails, ServiceCenterDetailsResponse, ServiceCenterQuery } from "./serviceCenterApi"
import { useAppSelector } from "../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../context/contextSelectors"
import { MainTopBarNewButton } from "../../../../shared/layouts/components/MainTopBarNewButton"
import { whenPrintingPageOnly } from "../../../../shared/components/print/printPageOnly"

const useStyles = makeStyles()(() => ({
    appbar: {
        transition: "background-color 0.2s ease, backdrop-filter 0.2s ease",
        backgroundColor: tailwindPalette.blue[50],
        backdropFilter: "blur(10px)",
        boxShadow: "none",
        ...whenPrintingPageOnly({ display: "none" }),
    },
    scrolled: {
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(6px)",
    },
    container: {
        padding: "0 2rem 0 1.3rem",
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
    },
    locationsButton: {
        color: "black",
        padding: "0 !important",
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
}))

const transformData = (data: ServiceCenterDetailsResponse) => data.serviceCenter

export function ServiceCenterTopBar() {
    const { classes, cx } = useStyles()
    const routeMatches = useMatches()
    const [isScrolled, setIsScrolled] = useState(false)
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const { data: details, isFetching } = useApiQuery(fetchServiceCenterDetails, {
        queryName: ServiceCenterQuery.SERVICE_CENTER_DETAILS,
        dependencies: { serviceCenterId: serviceCenterId ?? 0 },
        select: transformData,
        enabled: Boolean(serviceCenterId),
    })

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0)
        }
        window.addEventListener("scroll", handleScroll)

        return () => {
            window.removeEventListener("scroll", handleScroll)
        }
    }, [])

    const environmentRoutePath = routeMatches.find(
        (match) => isObjectWithProp(match.handle, "environmentRoot") && Boolean(match.handle.environmentRoot),
    )?.pathname

    return (
        <AppBar
            position="sticky"
            color="inherit"
            elevation={2}
            className={cx(classes.appbar, { [classes.scrolled]: isScrolled })}
        >
            <Grid className={classes.container}>
                <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing="3.3rem">
                        <Link className={classes.logoLink} to={SERVICE_CENTER_REPORTS_ROUTE}>
                            <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                        </Link>
                        {serviceCenterId && (
                            <Stack direction="row" alignItems="center" spacing=".5rem">
                                <FontAwesomeIcon color="black" icon={faLocationDot} />
                                {isFetching ? (
                                    <Skeleton variant="text" width={120} height={40} />
                                ) : (
                                    <Typography variant="h6">{details?.name}</Typography>
                                )}
                            </Stack>
                        )}
                        <ContextDisplay />
                    </Stack>

                    <Grid container item wrap="nowrap" width="auto" flexGrow={0} gap="1rem" alignItems="center">
                        <MainTopBarNewButton showDropdownMenu isServiceCenter />
                        <NotificationTopBarButton />
                        <AvatarMenu urlPrefix={environmentRoutePath} />
                    </Grid>
                </Grid>
            </Grid>
        </AppBar>
    )
}
